import React, { useMemo, useState } from 'react';
import { X, Copy, Check, ExternalLink } from 'lucide-react';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

function CopyField({ value, rows }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard permission denied — the textarea itself is still selectable/copyable manually
    }
  };
  return (
    <div className="flex items-start gap-2">
      {rows ? (
        <textarea readOnly rows={rows} value={value} className={`${inputClass} font-mono text-[11px] resize-none`} onFocus={(e) => e.target.select()} />
      ) : (
        <input readOnly value={value} className={`${inputClass} font-mono text-[12px]`} onFocus={(e) => e.target.select()} />
      )}
      <button
        onClick={copy}
        className="shrink-0 p-2 border border-[#D8DDE6] rounded-[4px] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3]/40 transition-colors"
        title="Copy"
      >
        {copied ? <Check size={15} className="text-[#04844B]" /> : <Copy size={15} />}
      </button>
    </div>
  );
}

/**
 * Phase 14 — distribution. Frontend-only: the public link and embed snippet
 * both just point at the existing public route (/forms/:id, PublicFormPage)
 * and public API, so nothing new needed server-side. Only meaningful once
 * a form is published — an unpublished form's public link 404s.
 */
export default function SharePanel({ form, onClose }) {
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');

  const publicUrl = `${window.location.origin}/forms/${form._id}`;

  const trackedUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (utmSource.trim()) params.set('utm_source', utmSource.trim());
    if (utmMedium.trim()) params.set('utm_medium', utmMedium.trim());
    if (utmCampaign.trim()) params.set('utm_campaign', utmCampaign.trim());
    const qs = params.toString();
    return qs ? `${publicUrl}?${qs}` : publicUrl;
  }, [publicUrl, utmSource, utmMedium, utmCampaign]);

  const embedCode = `<iframe src="${publicUrl}" width="100%" height="700" style="border:0;" title="${form.name.replace(/"/g, '&quot;')}"></iframe>`;

  const isPublished = form.status === 'published';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[520px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Share & Embed</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        {!isPublished ? (
          <p className="text-[12px] text-[#706E6B]">
            Publish this form first — its public link and embed code only work once it's live.
          </p>
        ) : (
          <div className="space-y-6">
            <div>
              <label className={labelClass}>Public Link</label>
              <CopyField value={publicUrl} />
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-[#0176D3] font-bold mt-1.5 hover:underline"
              >
                Open in new tab <ExternalLink size={11} />
              </a>
            </div>

            <div className="pt-4 border-t border-[#F0F1F3]">
              <label className={labelClass}>Embed on Your Website</label>
              <CopyField value={embedCode} rows={3} />
            </div>

            <div className="pt-4 border-t border-[#F0F1F3]">
              <label className={labelClass}>Link With Campaign Tracking</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                <input className={inputClass} placeholder="Source" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} />
                <input className={inputClass} placeholder="Medium" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} />
                <input className={inputClass} placeholder="Campaign" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} />
              </div>
              <CopyField value={trackedUrl} />
              <p className="text-[11px] text-[#706E6B] mt-1.5">
                Submissions through this link carry the source/medium/campaign into the submission record.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
